export type IngestRunStatus =
  | 'uploaded' | 'upload_failed' | 'validating' | 'validated'
  | 'validation_failed' | 'running' | 'completed' | 'failed';

/*
    How a file reached us (MPN-1634).

    All four the server can send. It was declared as two, so an s3 or old_platform run
    typed as something impossible -- harmless while nothing read the field and wrong the
    moment the Files page shows it as a column.
*/
export type IngestRunSource = 'manual_upload' | 'sftp' | 's3' | 'old_platform';

/** What Ops calls each one. Direct Upload rather than Manual Upload: the ticket and
 *  the upload button both say direct. */
export const INGEST_SOURCE_LABELS: Record<IngestRunSource, string> = {
  manual_upload: 'Direct Upload',
  sftp: 'SFTP',
  s3: 'S3',
  old_platform: 'Old Platform',
};

export class IngestDefinitionBrief {
  id: number;
  name: string;

  constructor() {
    this.id = 0;
    this.name = '';
  }
}

/*
    What the last load wrote that its row count hides (MPN-1969): contact points per
    contact column, and booleans read as no only because they matched no known value.
    A non-empty `warnings` on a completed run is "completed with warnings". Staff-only:
    null for customers.
*/
export interface LoadContactCount {
  column: string;
  kind: string;
  rows: number;
  written: number;
}

export interface LoadSummary {
  contacts: LoadContactCount[];
  unrecognised_booleans: { column: string; count: number; examples: string[] }[];
  warnings: string[];
}

export class IngestRun {
  id: number;
  file_name: string | null;
  s3_uri: string | null;
  status: IngestRunStatus;
  source: IngestRunSource;
  population_segment_name: string | null;
  row_count: number | null;
  run_date: string | null;
  /** The file's own date (YYYY-MM-DD); null until validation reads it. */
  as_of: string | null;
  create_date: string;
  ingest_definition: IngestDefinitionBrief | null;
  config_version: { id: number; version_number: number } | null;
  status_message: string;
  status_errors: string[];
  status_warnings: string[];
  /*
      MPN-1564. Null unless this run's spec has a primary (`depends_on`):
        - matched/unmatched: how many of the file's rows belong to the population.
        - waiting_on: the file this one is PARKED behind. A parked run sits at
          'validated' like an ordinary pre-load run, so this is the only thing that
          distinguishes "waiting for another file" from "ready to run".
  */
  matched_row_count: number | null;
  unmatched_row_count: number | null;
  waiting_on: string | null;
  // Derived server-side from the file's header (MPN-1583). Null = header never
  // recorded, which is not the same answer as false.
  is_test_file: boolean | null;
  load_summary: LoadSummary | null;

  constructor() {
    this.id = 0;
    this.file_name = null;
    this.s3_uri = null;
    this.status = 'uploaded';
    this.source = 'manual_upload';
    this.population_segment_name = null;
    this.row_count = null;
    this.run_date = null;
    this.as_of = null;
    this.create_date = '';
    this.ingest_definition = null;
    this.config_version = null;
    this.status_message = '';
    this.status_errors = [];
    this.status_warnings = [];
    this.matched_row_count = null;
    this.unmatched_row_count = null;
    this.waiting_on = null;
    this.is_test_file = null;
    this.load_summary = null;
  }
}

export class ValidationResult {
  errors: string[];
  warnings: string[];
  summary: Record<string, unknown>;

  constructor() {
    this.errors = [];
    this.warnings = [];
    this.summary = {};
  }
}

export class PresignResponse {
  id: number;
  url: string;
  s3_uri: string;
  key_name: string;

  constructor() {
    this.id = 0;
    this.url = '';
    this.s3_uri = '';
    this.key_name = '';
  }
}
